import { useEffect, useRef, useState } from 'react';

// A workspace that already rendered children with a confirmed subscription this
// session can hit a transient `currentSubData.data === null` reading (observed
// live: refetchOnMount:'always' racing a workspace-context resolve mid-session).
// Swapping {children} out instantly on that single bad reading unmounts the
// entire app tree — losing in-flight state like an unsent chat message. Only a
// FIRST-load unsubscribed workspace gates immediately; a flip from confirmed
// access waits this long for the reading to settle before trusting it.
export const SUBSCRIPTION_GATE_DEBOUNCE_MS = 4000;

/**
 * Debounces a raw `hasNoSubscription` reading so a transient false positive
 * (query briefly resolving with `data: null`) cannot instantly unmount the
 * protected app tree for a workspace that already had confirmed access.
 */
export function useDebouncedNoSubscriptionGate(
  hasNoSubscription: boolean,
  isSubLoading: boolean,
  hasSubscriptionData: boolean,
): boolean {
  const hadConfirmedAccessRef = useRef(false);
  const [confirmedNoSubscription, setConfirmedNoSubscription] = useState(false);

  useEffect(() => {
    if (!hasNoSubscription) {
      if (!isSubLoading && hasSubscriptionData) {
        hadConfirmedAccessRef.current = true;
      }
      setConfirmedNoSubscription(false);
      return undefined;
    }

    if (!hadConfirmedAccessRef.current) {
      setConfirmedNoSubscription(true);
      return undefined;
    }

    const timer = setTimeout(() => setConfirmedNoSubscription(true), SUBSCRIPTION_GATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [hasNoSubscription, isSubLoading, hasSubscriptionData]);

  return confirmedNoSubscription;
}
